// services/floating-notification.service.ts
import { Injectable, OnDestroy, PLATFORM_ID, Inject, DOCUMENT, Injector } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { HttpClient } from '@angular/common/http';
import { NotificationBellService } from "./notification-bell.service";
import { SystemApp } from "../../constans/SystemConfigurations";
import { ApiUrl } from "../../shared/models/apiurl.models";
import { Subject } from 'rxjs';
import { getFreshWsToken } from "../../services/ws-token";

import {SoundAlertService} from "../../services/sound-alert.service";

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
    private socket$: WebSocketSubject<any> | null = null;
    private destroy$ = new Subject<void>();
    private retryInterval = 3000; // Start with 3 seconds
    private maxRetryInterval = 30000; // Max 30 seconds between retries
    private isVisible = true;
    private connecting = false;
    private reconnectTimeout: any = null;
    private destroyed = false;
    // A handshake that never opened (403) forces ONE token refresh on the
    // next attempt, even if the token does not look expired locally.
    private forceRefresh = false;

    constructor(
        private http: HttpClient,
        private soundAlert: SoundAlertService,
        private notificationBellService: NotificationBellService,
        private apiUrl: ApiUrl,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: Object,
        private injector: Injector
    ) {
        this.initVisibilityHandler();
    }

    connect(): void {
        if (this.destroyed || this.socket$ || this.connecting) return;
        // POD1-6: credential in the Sec-WebSocket-Protocol header, not the URL.
        if (!this.apiUrl.getJWTTokenKey()) return;

        // SEC-12 follow-up (2026-11-01): a WebSocket handshake never passes
        // through the HTTP interceptor's 401-refresh, and the access token
        // lives 15 minutes. The old code read the token once and retryWhen()
        // re-opened the SAME socket config, so after expiry it presented the
        // dead token forever (hundreds of 403 / invalid_token per hour). Now
        // every attempt gets a valid token first (refreshing when expired -
        // single-flight, shared with the interceptor) and builds a new socket.
        const force = this.forceRefresh;
        this.forceRefresh = false;
        this.connecting = true;
        getFreshWsToken(this.injector, force).then(token => {
            this.connecting = false;
            if (token && !this.socket$ && !this.destroyed) this.open(token);
        });
    }

    private open(token: string): void {
        let opened = false;
        const socket$ = webSocket<any>({
            url: `${SystemApp.web_socket}notifications/`,
            protocol: ['bearer', token],
            openObserver: {
                next: () => {
                    opened = true;
                    this.retryInterval = 3000; // Reset retry interval on successful connection
                }
            }
        });
        this.socket$ = socket$;

        const dropped = (err?: any) => {
            if (this.socket$ !== socket$) return;
            this.socket$ = null;
            if (err) this.handleError(err); else console.log('WebSocket connection closed');
            if (!opened) this.forceRefresh = true;
            this.scheduleReconnect();
        };

        socket$.subscribe({
            next: (msg) => this.handleMessage(msg),
            error: (err) => dropped(err),
            complete: () => dropped()
        });
    }

    private scheduleReconnect(): void {
        if (this.destroyed || this.reconnectTimeout || !this.isVisible) return;
        const wait = this.retryInterval;
        this.retryInterval = Math.min(this.retryInterval * 2, this.maxRetryInterval);
        this.reconnectTimeout = setTimeout(() => {
            this.reconnectTimeout = null;
            this.connect();
        }, wait);
    }

    private handleMessage(msg: any): void {
        if (msg.type === 'new_notification') {
            this.soundAlert.playAlert('notification.mp3');
            this.notificationBellService.notifyNotifications();
        }
    }

    private handleError(err: Error): void {
        console.error('WebSocket error:', err);
    }

    private initVisibilityHandler(): void {
        if (typeof this.document !== 'undefined') {
            this.document.addEventListener('visibilitychange', () => {
                this.isVisible = !this.document.hidden;
                if (this.isVisible && !this.socket$) {
                    this.connect();
                }
            });
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        this.destroy$.next();
        this.destroy$.complete();
        const socket$ = this.socket$;
        this.socket$ = null;
        socket$?.complete();
    }
}
